import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Zap, History } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PRIORITIES } from "@/lib/taskConstants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const TRIGGERS = [
  { value: "task_created", label: "Se crea una tarea" },
  { value: "task_completed", label: "Se completa una tarea" },
  { value: "task_assigned", label: "Se asigna una tarea" },
  { value: "expense_created", label: "Se registra un gasto" },
  { value: "file_uploaded", label: "Se sube un archivo" },
  { value: "poll_closed", label: "Se cierra una encuesta" },
];
const TRIGGER_MAP = Object.fromEntries(TRIGGERS.map((t) => [t.value, t.label]));

const ACTIONS = [
  { value: "notify", label: "Enviar notificación" },
  { value: "post_message", label: "Publicar mensaje en el Canal General" },
  { value: "assign_task", label: "Asignar responsable (solo al crear tarea)" },
  { value: "move_kanban_end", label: "Mover al final del Kanban (solo al completar)" },
];
const ACTION_MAP = Object.fromEntries(ACTIONS.map((a) => [a.value, a.label]));

const TASK_TRIGGERS = ["task_created", "task_completed", "task_assigned"];

const CreateRuleDialog = ({ ws, members, projects, onDone }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("task_completed");
  const [actionType, setActionType] = useState("notify");
  const [target, setTarget] = useState("__all__");
  const [text, setText] = useState("");
  const [condProject, setCondProject] = useState("any");
  const [condPriority, setCondPriority] = useState("any");
  const [saving, setSaving] = useState(false);

  const isTaskTrigger = TASK_TRIGGERS.includes(trigger);
  const activeMembers = useMemo(() => members.filter((m) => m.status === "active"), [members]);
  const availableActions = useMemo(() => ACTIONS.filter((a) =>
    (a.value !== "assign_task" || trigger === "task_created") &&
    (a.value !== "move_kanban_end" || trigger === "task_completed")), [trigger]);

  const save = async () => {
    if (!name.trim()) return toast.error("Ponle un nombre a la regla");
    setSaving(true);
    try {
      await api.post(`/workspaces/${ws.id}/automations`, {
        name,
        trigger,
        action_type: actionType,
        action_target_id: ["notify", "assign_task"].includes(actionType) ? target : null,
        action_text: actionType === "post_message" ? text : null,
        condition_project_id: isTaskTrigger && condProject !== "any" ? condProject : null,
        condition_priority: isTaskTrigger && condPriority !== "any" ? condPriority : null,
      });
      toast.success("Regla creada");
      onDone();
      setOpen(false);
      setName(""); setText("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="automation-create-btn"><Plus className="h-4 w-4 mr-1" /> Nueva regla</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="automation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Nueva automatización</DialogTitle>
          <DialogDescription>Cuando ocurra X → hacer Y. Simple y sin sorpresas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input data-testid="automation-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Avisar cuando se complete una tarea" maxLength={120} />
          </div>
          <div className="space-y-2">
            <Label>Cuando… (disparador)</Label>
            <Select value={trigger} onValueChange={(v) => { setTrigger(v); if (actionType === "assign_task" && v !== "task_created") setActionType("notify"); if (actionType === "move_kanban_end" && v !== "task_completed") setActionType("notify"); }}>
              <SelectTrigger data-testid="automation-trigger-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRIGGERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isTaskTrigger && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Solo en el proyecto</Label>
                <Select value={condProject} onValueChange={setCondProject}>
                  <SelectTrigger data-testid="automation-project-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Cualquier proyecto</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.icon} {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Solo con prioridad</Label>
                <Select value={condPriority} onValueChange={setCondPriority}>
                  <SelectTrigger data-testid="automation-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Cualquier prioridad</SelectItem>
                    {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Entonces… (acción)</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger data-testid="automation-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableActions.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {actionType === "notify" && (
            <div className="space-y-2">
              <Label>Notificar a</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="automation-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los miembros</SelectItem>
                  {isTaskTrigger && <SelectItem value="__assignee__">El responsable de la tarea</SelectItem>}
                  {activeMembers.map((m) => (
                    <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {actionType === "assign_task" && (
            <div className="space-y-2">
              <Label>Asignar a</Label>
              <Select value={target === "__all__" || target === "__assignee__" ? "" : target} onValueChange={setTarget}>
                <SelectTrigger data-testid="automation-assignee-select"><SelectValue placeholder="Elige una persona" /></SelectTrigger>
                <SelectContent>
                  {activeMembers.map((m) => (
                    <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {actionType === "post_message" && (
            <div className="space-y-2">
              <Label>Texto del mensaje</Label>
              <Input data-testid="automation-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ej: ¡Tarea completada! 🎉" maxLength={500} />
            </div>
          )}
          <Button className="w-full" onClick={save} disabled={saving} data-testid="automation-save-btn">
            {saving ? "Guardando…" : "Crear regla"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AutomationsTab = ({ ws, members }) => {
  const qc = useQueryClient();
  const [showRuns, setShowRuns] = useState(false);

  const { data: rules = [] } = useQuery({
    queryKey: ["automations", ws.id],
    queryFn: async () => (await api.get(`/workspaces/${ws.id}/automations`)).data,
  });
  const { data: projects = [] } = useQuery({
    queryKey: ["projects", ws.id],
    queryFn: async () => (await api.get(`/workspaces/${ws.id}/projects`)).data,
  });
  const { data: runs = [] } = useQuery({
    queryKey: ["automation-runs", ws.id],
    queryFn: async () => (await api.get(`/workspaces/${ws.id}/automations/runs`)).data,
    enabled: showRuns,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["automations", ws.id] });

  const toggle = async (rule) => {
    try {
      await api.put(`/automations/${rule.id}`, { enabled: !rule.enabled });
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (rule) => {
    try {
      await api.delete(`/automations/${rule.id}`);
      toast.success("Regla eliminada");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="automations-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Automatizaciones</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Reglas simples "Cuando X → hacer Y" dentro del Espacio</p>
        </div>
        <CreateRuleDialog ws={ws} members={members} projects={projects} onDone={refresh} />
      </div>

      {rules.length === 0 ? (
        <Card className="p-8 text-center space-y-2 border-dashed" data-testid="automations-empty">
          <Zap className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-sm font-medium">Sin automatizaciones todavía</p>
          <p className="text-xs text-muted-foreground">Crea tu primera regla, por ejemplo: "Cuando se completa una tarea → avisar a todos".</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {rules.map((r) => (
            <Card key={r.id} className={`p-4 flex items-center gap-3 ${!r.enabled ? "opacity-60" : ""}`} data-testid={`automation-rule-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{r.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {TRIGGER_MAP[r.trigger]} → {ACTION_MAP[r.action_type]}
                </p>
              </div>
              <Badge variant="outline" className="text-xs font-normal shrink-0">{r.run_count} ejecuciones</Badge>
              <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`automation-toggle-${r.id}`} />
              <Button variant="ghost" size="sm" className="text-destructive h-8 w-8 p-0" onClick={() => remove(r)} data-testid={`automation-delete-${r.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Button variant="ghost" size="sm" onClick={() => setShowRuns((s) => !s)} data-testid="automation-runs-toggle">
        <History className="h-4 w-4 mr-1" /> {showRuns ? "Ocultar historial" : "Ver historial de ejecuciones"}
      </Button>
      {showRuns && (
        <Card className="p-4 space-y-2" data-testid="automation-runs-list">
          {runs.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aún no se ha ejecutado ninguna regla.</p>
          ) : (
            runs.map((run) => (
              <div key={run.id} className="flex items-center gap-2 text-xs border-b border-border/40 last:border-0 pb-2 last:pb-0">
                <Badge variant={run.status === "ok" ? "secondary" : "destructive"} className="text-[10px] shrink-0">{run.status}</Badge>
                <span className="font-medium shrink-0">{run.rule_name}</span>
                <span className="text-muted-foreground truncate flex-1">{run.entity_label} · {run.detail}</span>
                <span className="text-muted-foreground shrink-0">{new Date(run.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))
          )}
        </Card>
      )}
    </div>
  );
};
